/**
 * UserEmailManagementModal Component
 *
 * Modal component for managing user email templates (password reset, account status, MFA, etc.)
 *
 * NOTE: User invitation/activation emails have been moved to Beta Applications workflow.
 */

import React from 'react';
import { Mail } from 'lucide-react';

import {
  Modal,
  ModalContent,
  ModalBody,
  Card,
  CardHeader,
  CardBody
} from '@enfomentis/ui';

import { UserTemplateEditing } from './UserTemplateEditing';

/**
 * Props for UserEmailManagementModal component
 */
export interface UserEmailManagementModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Function to close the modal */
  onClose: () => void;
}

/**
 * User Email Management Modal
 *
 * Features:
 * - Email template management with preview and validation
 * - Select dropdown for choosing template type
 * - Mobile-responsive design
 * - Integration with existing user management infrastructure
 *
 * Usage:
 * ```tsx
 * <UserEmailManagementModal
 *   isOpen={isModalOpen}
 *   onClose={closeModal}
 * />
 * ```
 */
export const UserEmailManagementModal: React.FC<UserEmailManagementModalProps> = ({
  isOpen,
  onClose
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="5xl"
      scrollBehavior="inside"
      closeButton
    >
      <ModalContent className="h-screen">
        {() => (
          <ModalBody className="p-0 flex-1">
            <Card shadow="none" radius="none" fullWidth className="h-full flex flex-col">
              {/* Header */}
              <CardHeader className="flex-shrink-0 border-b border-default-200">
                <div className="flex items-center gap-2">
                  <Mail size={20} className="text-primary" />
                  <h2 className="text-lg font-semibold text-text-primary">
                    User Email Templates
                  </h2>
                </div>
              </CardHeader>

              {/* Content */}
              <CardBody className="flex-1 p-0 overflow-hidden">
                <UserTemplateEditing />
              </CardBody>
            </Card>
          </ModalBody>
        )}
      </ModalContent>
    </Modal>
  );
};